import { useSelectedClient } from '@entities/clients/heplers.ts';
import { useGetClientByIdQuery } from '@entities/clients/rtk-query.ts';
import type { ClientsListQueryParams } from '@entities/clients/types.ts';
import { useColorTags } from '@entities/common/index.ts';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import * as st from '@pages/clients/clients-page/styles.module.scss';
import { ClientCard, Note } from '@widgets/clients/index.ts';
import { Profile } from '@features/clients/client-profile/ui.tsx';
import { ColorTagsList } from '@features/clients/colors-list/index.ts';
import {
  AcquiredEventsList,
  ClientList,
  ConsultationList,
  ContactsList,
  GroupsList,
  NoteList,
  RentedArticleList,
  RentedProgramList,
  Tabs,
} from '@features/clients/index.ts';
import { useWindowWidth } from '@shared/hooks/index.ts';
import { Card, ListBody } from '@shared/ui/index.ts';
import { Loader } from '@src/library/components/common/index.ts';
import type { Note as Notemodel } from '@src/library/models/clients.ts';

type ClientInfoWithListPT = {
  listParams: Omit<ClientsListQueryParams, 'page'>;
  searchNotes: string;
  onSearchNotes: (searchNotes: string) => void;
  isHeaderWithToLines?: boolean;
};

export const ClientInfoWithList = ({
  listParams,
  searchNotes,
  onSearchNotes,
  isHeaderWithToLines,
}: ClientInfoWithListPT) => {
  const windowWidth = useWindowWidth();
  const { selectedClient, clearSelectedClient } = useSelectedClient();

  // useLayoutEffect(() => {
  //   clearSelectedClient();
  // }, []);

  const { colorTags, selectedColorTag, setSelectedColorTag, resetSelectedColorTag } =
    useColorTags();

  const [showRightBlock, setShowRightBlock] = useState<boolean>(false);
  const {
    data: client,
    isLoading,
    // isFetching,
  } = useGetClientByIdQuery(selectedClient?.id, { skip: !selectedClient });

  const [onlyOneChildNode, setOnlyOneChildNode] = useState(false);

  useEffect(() => {
    const clients = document.querySelector('#clientsWrapperElement');
    if (clients) {
      setOnlyOneChildNode(clients.children.length === 1);
    }
  }, [showRightBlock, selectedClient, client]);

  return (
    <ListBody className={cn(isHeaderWithToLines && st.bodyWithExtendedPadding)}>
      <div
        id="clientsWrapperElement"
        className={cn(st.clients, onlyOneChildNode && st.onlyOneChild)}
      >
        <ClientList params={listParams} setShowRightBlock={setShowRightBlock} />

        {windowWidth > 768 && showRightBlock && selectedClient && (
          <Card className={st.client}>
            {!client || isLoading ? (
              <Loader />
            ) : (
              <>
                <ClientCard selectedColorTag={selectedColorTag!} client={client} />
                <Tabs
                  client={client}
                  searchNotes={searchNotes}
                  onSearchNotes={onSearchNotes}
                  Profile={(id: number) => <Profile key={id} id={id} />}
                  ContactsList={(id: number) => <ContactsList id={id} />}
                  Notes={(id: number, searchNotes: string) => (
                    <NoteList key={id} id={id} searchNotes={searchNotes}>
                      {(note: Notemodel) => <Note key={note.id} note={note} />}
                    </NoteList>
                  )}
                  RentedPrograms={(id, code) => (
                    <RentedProgramList key={`${id}-programs`} id={id} code={code} />
                  )}
                  RentedArticles={(id) => (
                    <RentedArticleList key={`${id}-articles`} clientId={id} />
                  )}
                  AssignedConsultations={(id, code) => (
                    <ConsultationList key={`${id}-${code}`} id={id} />
                  )}
                  EventsList={(id, code) => (
                    <AcquiredEventsList key={`${id}-${code}`} id={id} code={code} />
                  )}
                  GroupsList={(id: number) => <GroupsList id={id} />}
                  ColorsList={(id: number) => (
                    <ColorTagsList
                      id={Number(id)}
                      colorTags={colorTags}
                      selectedColorTag={selectedColorTag!}
                      setSelectedColorTag={setSelectedColorTag}
                      resetSelectedColorTag={resetSelectedColorTag}
                    />
                  )}
                />
              </>
            )}
          </Card>
        )}
      </div>
      <Outlet />
    </ListBody>
  );
};
